/*
 * Created on 06.04.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from '../i18n/language.service';
import { Page } from './table-page-sort.model';
import { I18N } from './table-pagination.component.i18n';

/**
 * Diese Komponente ist eine interne Komponente welche eine paginierung der daten liste darstellt.
 *
 * @author imam
 */
export interface IPage {
    label: string;
    value: any;
}

@Component({
    selector: 'common-table-pagination',
    templateUrl: 'table-pagination.component.html'
})
export class TablePaginationComponent implements OnChanges, OnInit, OnDestroy {
    @Input() page: Page;
    @Output() pageChange: EventEmitter<any> = new EventEmitter();
    @Input() showPageSize: boolean;
    pages: IPage[] = [];
    i18n: any;
    paginationRange: boolean = false;
    isopen: boolean = false;
    languageSubscription: Subscription;
    pageArray = [];


    constructor(private languageService: LanguageService) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnInit() {
        this.getPaginationLength();
        this.page.selectedPage = this.page.number + 1;
    }

    ngOnChanges() {
        this.page.last = this.page.number + 1 === this.page.totalPages;
        this.page.first = this.page.number === 0;
        this.pageArray = this.getPageArray();
    }

    ngOnDestroy() {
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }

    /**
     * Go to the first page
     */
    first() {
        this.setCurrent(0);
    }

    /**
     * Go to the previous page
     */
    previous() {
        if (this.getCurrent() > 0) {
            this.setCurrent(this.getCurrent() - 1);
        }
    }

    /**
     * Go to the next page
     */
    next() {
        if (this.page.last) {
            return;
        }
        this.setCurrent(this.getCurrent() + 1);
    }

    /**
     * Go to the last page
     */
    last() {
        this.setCurrent(this.page.totalPages - 1);
    }

    /**
     * Set the current page number.
     */
    setCurrent(page: number) {
        if (this.page.number !== page) {
            this.page.number = page;
            this.pageChange.emit(this.page);
        }
    }

    /**
     * Get the current page number.
     */
    getCurrent(): number {
        return this.page.number;
    }

    getPage() {
        this.page.number = this.page.selectedPage - 1;
        if (this.page.number >= 0 && this.page.number < this.page.totalPages) {
            this.pageChange.emit(this.page);
        }
    }

    onChange(value: any) {
        this.page.number = 0;
        this.page.size = value;
        this.pageChange.emit(this.page);
        this.pageArray = this.getPageArray();
    }

    /**
     * get totalpages
     */
    getPaginationLength() {
        const p = this.page.totalPages;
        this.pages = Array(p);
        return this.pages;
    }

    getPageArray(): IPage[] {
        const currentPage = this.page.number;
        const itemsPerPage = this.page.size;
        const totalItems = this.page.totalElements;
        const paginationRange = 6;
        const pages = [];
        const totalPages = Math.ceil(totalItems / itemsPerPage);
        const halfWay = Math.ceil(paginationRange / 2);

        const isStart = currentPage <= halfWay;
        const isEnd = totalPages - halfWay <= currentPage;
        const isMiddle = !isStart && !isEnd;

        const ellipsesNeeded = paginationRange < totalPages;
        let i = 1;

        while (i <= totalPages && i <= paginationRange) {
            let label;
            const pageNumber = TablePaginationComponent.calculatePageNumber(i, currentPage, paginationRange, totalPages);
            const openingEllipsesNeeded = (i === 2 && (isMiddle || isEnd));
            const closingEllipsesNeeded = (i === paginationRange - 1 && (isMiddle || isStart));
            if (ellipsesNeeded && (openingEllipsesNeeded || closingEllipsesNeeded)) {
                label = '...';
            } else {
                label = pageNumber;
            }
            pages.push({
                label: label,
                value: pageNumber
            });
            i++;
        }
        return pages;
    }


    private static calculatePageNumber(i: number, currentPage: number, paginationRange: number, totalPages: number) {
        const halfWay = Math.ceil(paginationRange / 2);
        if (i === paginationRange) {
            return totalPages;
        } else if (i === 0) {
            return i;
        } else if (paginationRange < totalPages) {
            if (totalPages - halfWay < currentPage) {
                return totalPages - paginationRange + i;
            } else if (halfWay < currentPage) {
                return currentPage - halfWay + i;
            } else {
                return i;
            }
        } else {
            return i;
        }
    }
}
